import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  
  initialize() {
    // Initialize validation state
    this.validationState = {
      1: { valid: false, fields: {}, completed: false, touched: {} },
      2: { valid: false, fields: {}, completed: false, touched: {} },
      3: { valid: true, fields: {}, completed: true, touched: {} }, // Assume valid by default
      4: { valid: false, fields: {}, completed: false, touched: {} }
    };
    
    // Find the checkout modal
    this.checkoutModal = document.getElementById('checkout-modal');
    this.setupPanelObserver(); // Set up observers for panel changes
    this.setupValidationListeners(); // Set up input validation listeners
    this.setupButtonListeners(); // Listen for checkout button clicks
    // After everything is set up, check the initial state of fields
    this.checkInitialState();
  }
  
  // Check initial field states
  checkInitialState(showErrors = false) {
    // Check if any order type radio is already selected
    if (this.isOrderTypeSelected()) {
      this.validationState[1].fields.orderType = true;
      this.validationState[1].touched.orderType = true;
    }
    
    // Check car instructions if bring-to-car is checked
    const bringToCarCheck = document.querySelector('.do-bring-to-car-check');
    if (bringToCarCheck && bringToCarCheck.checked) {
      this.validateCarInstructions(showErrors);
    } else {
      this.validationState[1].fields.carInstructions = true;
    }
    
    // Run validation on panes without showing errors yet
    this.validateCurrentPane(1);
    this.validateCurrentPane(2);
    this.validateCurrentPane(4);
  }
  
  setupPanelObserver() {
    // Create a MutationObserver to watch for panel changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style' && mutation.target.classList.contains('checkout-panel')) {
          const panes = document.querySelectorAll('.checkout-panel');
          panes.forEach((pane) => {
            if (pane.style.display !== 'none') {
              const paneNumber = parseInt(pane.getAttribute('data-pane'));
              this.validateCurrentPane(paneNumber);
            }
          });
        }
      });
    });
    
    // Start observing checkout panels
    const checkoutPanels = document.querySelectorAll('.checkout-panel');
    checkoutPanels.forEach((panel) => {
      observer.observe(panel, { attributes: true });
    });
  }
  
  setupValidationListeners() {
    // Pane 1: Cart Review
    this.setupPane1Validation();
    
    // Pane 2: Personal Info
    this.setupPane2Validation();
    
  }
  
  setupButtonListeners() {
    // Next button
    const nextButton = document.querySelector('.checkout-next');
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        if (this.validationState[1].valid) {
          this.validationState[1].completed = true;
        }
        this.validateBeforeNext(1);
      });
    }
    
    // Payment button
    const paymentButton = document.querySelector('.checkout-payment');
    if (paymentButton) {
      paymentButton.addEventListener('click', () => {
        if (this.validationState[2].valid) {
          this.validationState[2].completed = true;
        }
        this.validateBeforeNext(2);
      });
    }
    
    // Verify button
    const verifyButton = document.querySelector('.checkout-verify');
    if (verifyButton) {
      verifyButton.addEventListener('click', () => {
        if (this.validationState[4].valid) {
          this.validationState[4].completed = true;
        }
        this.validateBeforeNext(4);
      });
    }
    
    // Place order button
    const placeButton = document.querySelector('.checkout-place');
    if (placeButton) {
      placeButton.addEventListener('click', () => {
        if (this.validationState[3].valid) {
          this.validationState[3].completed = true;
        }
        this.validateBeforeNext(3);
      });
    }
  }
  
  setupPane1Validation() {
    // 1. Validate order type selection
    const orderTypeRadios = document.querySelectorAll('input[name="order-type"]');
    orderTypeRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.validationState[1].fields.orderType = true;
        this.validateCurrentPane(1);
      });
    });
    
    // 2. Validate future order selections, if visible
    const futureOrderDatepicker = document.getElementById('future-order-datepicker');
    if (futureOrderDatepicker) {
      futureOrderDatepicker.addEventListener('change', () => {
        this.validationState[1].fields.futureDate = true;
        this.validateCurrentPane(1);
      });
    }
    
    const futureOrderTimeSelect = document.getElementById('future-order-time-select');
    if (futureOrderTimeSelect) {
      futureOrderTimeSelect.addEventListener('change', () => {
        this.validationState[1].fields.futureTime = true;
        this.validateCurrentPane(1);
      });
    }
    
    // 3. Validate car instructions, if bring to car is selected
    const bringToCarCheck = document.querySelector('.do-bring-to-car-check');
    const carInstructions = document.querySelector('.car-instructions');
    
    if (bringToCarCheck && carInstructions) {
      bringToCarCheck.addEventListener('change', () => {
        if (bringToCarCheck.checked) {
          // Don't immediately validate on check to avoid premature error state
          // Just set up the input listener
          carInstructions.addEventListener('input', this.validateCarInstructions.bind(this));
          
          // Set car instructions to neutral state until user interacts with it
          this.validationState[1].fields.carInstructions = true;
          this.validateCurrentPane(1);
          
          // Add class to indicate it's required but not yet invalid
          carInstructions.classList.add('required-field');
        } else {
          // When unchecked, remove any validation error
          carInstructions.classList.remove('is-invalid', 'required-field');
          this.validationState[1].fields.carInstructions = true;
          this.validateCurrentPane(1);
        }
      });
      
      carInstructions.addEventListener('input', this.validateCarInstructions.bind(this));
    }
  }
  
  validateCarInstructions() {
    const carInstructions = document.querySelector('.car-instructions');
    if (carInstructions) {
      const isBringToCarChecked = document.querySelector('.do-bring-to-car-check')?.checked;
      
      // Only validate car instructions if bring-to-car is checked
      if (isBringToCarChecked) {
        // Only mark as invalid if user has started typing something but it's too short
        if (carInstructions.value.trim() !== '' && carInstructions.value.trim().length < 5) {
          carInstructions.classList.add('is-invalid');
          this.validationState[1].fields.carInstructions = false;
        } else {
          carInstructions.classList.remove('is-invalid');
          this.validationState[1].fields.carInstructions = true;
        }
      } else {
        // If checkbox is not checked, no validation needed
        carInstructions.classList.remove('is-invalid');
        this.validationState[1].fields.carInstructions = true;
      }
      
      this.validateCurrentPane(1);
    }
  }
  
  setupPane2Validation() {
    // Email validation
    const emailInput = document.querySelector('.delivery-email');
    if (emailInput) {
      emailInput.addEventListener('input', this.validateEmail.bind(this));
      emailInput.addEventListener('blur', () => {
        this.validationState[2].touched.email = true;
        this.validateEmail();
      });
    }

    // Name validation
    const nameInput = document.querySelector('.delivery-name');
    if (nameInput) {
      nameInput.addEventListener('input', this.validateName.bind(this));
      nameInput.addEventListener('blur', () => {
        this.validationState[2].touched.name = true;
        this.validateName();
      });
    }
    
    // Phone validation
    const phoneInput = document.querySelector('.phone-number');
    if (phoneInput) {
      // We have to use JQuery here because the phone number input uses a JQuery plugin (.formatter())
      $('.phone-number').on('input', () => { this.validatePhone() });
      $('.phone-number').on('blur', () => {
        this.validationState[2].touched.phone = true;
        this.validatePhone();
      });
    }

    // Promo code validation (optional)
    const promoInput = document.querySelector('.promo-code');
    if (promoInput) {
      promoInput.addEventListener('input', () => {
        // Promo code is optional
        this.validationState[2].fields.promo = true;
        this.validateCurrentPane(2);
      });
    }
    
    // Tip amount validation
    const tipInput = document.querySelector('.tip-amount');
    if (tipInput) {
      tipInput.addEventListener('input', this.validateTip.bind(this));
    }
  }
  
  validateEmail() {
    const emailInput = document.querySelector('.delivery-email');
    if (this.isEmailValid()) {
      emailInput.classList.remove('is-invalid');
      this.validationState[2].fields.email = true;
    } else {
      emailInput.classList.add('is-invalid');
      this.validationState[2].fields.email = false;
    }
    
    this.validateCurrentPane(2);
  }
  
  validateName() {
    const nameInput = document.querySelector('.delivery-name');
    if (this.isNameValid() && nameInput.value.trim().length >= 2) {
      nameInput.classList.remove('is-invalid');
      this.validationState[2].fields.name = true;
    } else {
      nameInput.classList.add('is-invalid');
      this.validationState[2].fields.name = false;
    }
    
    this.validateCurrentPane(2);
  }
  
  validatePhone() {
    const phoneInput = document.querySelector('.phone-number');
    if (this.isPhoneValid() && phoneInput.value.replace(/\D/g, '').length === 10) {
      phoneInput.classList.remove('is-invalid');
      this.validationState[2].fields.phone = true;
    } else if (!this.validationState[2].touched.phone) {
      phoneInput.classList.add('is-invalid');
      this.validationState[2].fields.phone = false;
    }

    this.validateCurrentPane(2);
  }
  
  validateTip() {

    const tipInput = document.querySelector('.tip-amount');
    if (tipInput) {
      const tip = tipInput.value.replace(/[^0-9\.]/g, '');
      const tipValue = parseFloat(tip);
      
      if (!isNaN(tipValue) && tipValue >= 0) {
        tipInput.classList.remove('is-invalid');
        this.validationState[2].fields.tip = true;
      } else {
        tipInput.classList.add('is-invalid');
        this.validationState[2].fields.tip = false;
      }
    }
    this.validateCurrentPane(2);
  }


  validatePrefilledFieldsForPane2() {
    const emailInput = document.querySelector('.delivery-email');
    const nameInput = document.querySelector('.delivery-name');
    const phoneInput = document.querySelector('.phone-number');
    const email = emailInput ? emailInput.value.trim() : '';
    const name = nameInput ? nameInput.value.trim() : '';
    const phone = phoneInput ? phoneInput.value.replace(/\D/g, '') : '';


    if (this.isEmailValid()) {
      this.validationState[2].fields.email = email;
      this.validationState[2].touched.email = true;
    } 

    if (this.isNameValid()) {
      this.validationState[2].fields.name = name;
      this.validationState[2].touched.name = true;
    }
    
    // Check phone field
    if (this.isPhoneValid()) {
      this.validationState[2].fields.phone = phone;
      this.validationState[2].touched.phone = true;
    }
  }
  
  validateCurrentPane(paneNumber) {
    // Check if all required fields in the pane are valid
    switch (paneNumber) {
      case 1:
        // Panel 1 validation (Cart Review)
        const orderTypeSelected = this.isOrderTypeSelected();
        
        // Check if delivery minimum message is visible
        const hasMinimumOrderIssue = this.hasDeliveryMinimumMessage();
        
        // Order is valid only if order type is selected, car instructions are valid, 
        // and there's no minimum order issue
        this.validationState[1].valid = 
          !hasMinimumOrderIssue &&
          (this.validationState[1].fields.orderType || orderTypeSelected) && 
          (this.validationState[1].fields.carInstructions !== false);
        
        break;
      
      case 2:
        // Panel 2 validation (Personal Info)
        this.validatePrefilledFieldsForPane2();
        this.validationState[2].valid = (this.isEmailValid() && this.isNameValid() && this.isPhoneValid());
        break;
      
      case 3:
        // Panel 3 validation (Payment)
        // assume valid (we manage in checkout.js)
        break;
      
      case 4:
        // Panel 4 validation (Age Verification)
        // age verify flow is complex. assume valid (we manage in checkout.js)
        break;
    }
    
    // Update next button state
    this.updateNextButtonState(paneNumber);
  }
  
  isOrderTypeSelected() {
    return document.querySelector('input[name="order-type"]:checked') !== null || 
           document.querySelector('.tw-vroom-radio-btn-active') !== null;
  }
  
  isEmailValid() {
    const emailInput = document.querySelector('.delivery-email');
    console.log("isEmailValid", emailInput);
    console.log("isEmailValid value", emailInput.value);
    if (!emailInput) return false;
    // if (!this.validationState[2].touched.email) return true; // Don't validate if not touched
    const email = emailInput.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  isNameValid() {
    const nameInput = document.querySelector('.delivery-name');
    if (!nameInput) return false;
    // if (!this.validationState[2].touched.name) return true; // Don't validate if not touched
    return nameInput.value.trim().length >= 2;
  }
  
  isPhoneValid() {
    const phoneInput = document.querySelector('.phone-number');
    if (!phoneInput) return true;
    // if (!this.validationState[2].touched.phone) return true; // Don't validate if not touched
    const phone = phoneInput.value.replace(/\D/g, '');
    return phone.length === 10;
  }
  
  updateNextButtonState(paneNumber) {
    const validClasses = '!tw-bg-vroom-green-500 hover:!tw-bg-vroom-green-600';
    const invalidClasses = '!tw-bg-gray-400 hover:!tw-bg-gray-500';
    
    switch (paneNumber) {
      case 1:
        const nextButton = document.querySelector('.checkout-next');
        if (nextButton) {
          nextButton.disabled = !this.validationState[1].valid;
          this.updateButtonStyle(nextButton, this.validationState[1].valid, validClasses, invalidClasses);
        }
        break;
        
      case 2:
        const paymentButton = document.querySelector('.checkout-payment');
        if (paymentButton) {
          paymentButton.disabled = !this.validationState[2].valid;
          this.updateButtonStyle(paymentButton, this.validationState[2].valid, validClasses, invalidClasses);
        }
        break;

      case 4:
        const verifyButton = document.querySelector('.checkout-verify');
        if (verifyButton) {
          verifyButton.disabled = !this.validationState[4].valid;
          this.updateButtonStyle(verifyButton, this.validationState[4].valid, validClasses, invalidClasses);
        }
        break;
        
      default:
          break;
    }
  }
  
  // Add a new helper method to handle button styling
  updateButtonStyle(button, isValid, validClasses, invalidClasses) {
    if (isValid) {
      // Remove invalid classes and add valid classes
      button.classList.remove(...invalidClasses.split(' '));
      button.classList.add(...validClasses.split(' '));
    } else {
      // Remove valid classes and add invalid classes
      button.classList.remove(...validClasses.split(' '));
      button.classList.add(...invalidClasses.split(' '));
    }
  }
  
  validateBeforeNext(paneNumber) {
    this.validateCurrentPane(paneNumber);
    
    if (this.validationState[paneNumber].valid) {
      this.validationState[paneNumber].completed = true;
    }
  }
  
  // Add new helper method to check for delivery minimum message
  hasDeliveryMinimumMessage() {
    const minimumMessage = document.querySelector('.delivery-minimum-message');
    // Return true if element exists and is visible (not display:none)
    return minimumMessage !== null && 
           window.getComputedStyle(minimumMessage).display !== 'none';
  }
}
